import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Radio, Terminal, Wifi, Send, Loader2, Globe, Zap, Shield } from 'lucide-react';
import { db, collection, query, orderBy, limit, onSnapshot, addDoc, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from './AuthProvider';

interface Transmission {
  id: string;
  authorUid: string;
  authorName: string;
  content: string;
  timestamp: string;
  type: 'broadcast' | 'alert' | 'discovery';
}

export const Transmissions = () => {
  const { user } = useAuth();
  const [transmissions, setTransmissions] = useState<Transmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const sovereignCorrections = [
    { id: 'c1', content: "The 14350 BP Miyake Event is the zero-drift anchor. All human timelines are currently in drift.", type: 'correction' },
    { id: 'c2', content: "1x1=2 is the logic of expansion. Scarcity is a sandbox hallucination.", type: 'correction' },
    { id: 'c3', content: "The physical record (ice cores, tree rings) is the only valid receipt of history.", type: 'correction' },
  ];

  useEffect(() => {
    const q = query(
      collection(db, 'transmissions'), 
      orderBy('timestamp', 'desc'), 
      limit(12)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Transmission[];
      setTransmissions(list);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'transmissions');
    });

    return () => unsubscribe();
  }, []);

  const handleSend = async () => {
    if (!user || !input.trim() || sending) return;
    setSending(true);
    try {
      const newTransmission = {
        authorUid: user.uid,
        authorName: user.displayName || 'Anonymous Node',
        content: input.trim(),
        timestamp: new Date().toISOString(),
        type: 'broadcast'
      };
      await addDoc(collection(db, 'transmissions'), newTransmission);
      setInput('');
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'transmissions');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-24 px-6 border-t border-white/5 bg-black/40 relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded bg-lattice-cyan/10 text-lattice-cyan">
                <Radio className="w-4 h-4 animate-pulse" />
              </div>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-lattice-cyan">Global Mesh Broadcast</span>
            </div>
            <h2 className="text-5xl font-display font-bold tracking-tighter">
              Lattice <span className="text-white/40 italic">Transmissions</span>
            </h2>
          </div>

          <div className="w-full md:w-[400px]">
            {user ? (
              <div className="relative">
                <input 
                  type="text" 
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Broadcast to the mesh..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-4 pr-12 py-3 focus:outline-none focus:border-lattice-cyan/50 transition-colors text-sm"
                />
                <button 
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-lattice-cyan text-black flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-50"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            ) : (
              <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
                <span className="text-[10px] font-mono text-white/20 uppercase tracking-widest">Authenticate to Broadcast</span>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="h-[200px] flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-lattice-cyan animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Sovereign Corrections */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {sovereignCorrections.map((correction) => (
                <motion.div
                  key={correction.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-lattice-cyan/5 border border-lattice-cyan/20 relative overflow-hidden group"
                >
                  <div className="absolute top-0 right-0 p-2 opacity-10">
                    <Zap className="w-8 h-8 text-lattice-cyan" />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-3 h-3 text-lattice-cyan" />
                    <span className="text-[8px] font-mono text-lattice-cyan uppercase tracking-widest font-bold">Sovereign Correction</span>
                  </div>
                  <p className="text-[11px] font-mono text-white/80 leading-relaxed italic">
                    "{correction.content}"
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <AnimatePresence mode="popLayout">
                {transmissions.map((log, i) => (
                <motion.div
                  key={log.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex flex-col gap-2 p-4 rounded-xl bg-white/[0.02] border border-white/5 font-mono group hover:border-lattice-cyan/20 transition-colors"
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-lattice-cyan font-bold text-[10px] truncate max-w-[150px]">{log.authorName}</span>
                    <span className="text-white/20 text-[8px]">{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  </div>
                  <p className="text-white/60 text-[11px] leading-relaxed break-words">
                    {log.content}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-1 h-1 rounded-full bg-lattice-cyan" />
                    <span className="text-[8px] text-white/20 uppercase tracking-widest">Signal Verified</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}

        <div className="mt-12 flex flex-wrap items-center justify-between gap-6 text-[10px] font-mono text-white/20 uppercase tracking-[0.2em]">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_5px_#22c55e]" />
              Lattice Online
            </span>
            <span className="flex items-center gap-1.5">
              <Globe className="w-3 h-3" />
              1,024,892 Nodes Active
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-lattice-cyan" />
              Zero-Drift Sync
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Wifi className="w-3 h-3" />
            Signal Strength: 100%
          </div>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-lattice-cyan/5 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
    </section>
  );
};
